import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '@/context/AdminAuthContext';

const navLinks = [
  { label: 'Services', href: '#services' },
  { label: 'About', href: '#about' },
  { label: 'Clients', href: '#clients' },
  { label: 'Projects', href: '#projects' },
  { label: 'Team', href: '#team' },
  { label: 'Contact', href: '#contact' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { isAdmin, setShowLoginModal } = useAdminAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNav = (href: string) => {
    setMenuOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-300 ${
        scrolled ? 'bg-white shadow-sm' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-16 md:h-20">
        {/* Logo */}
        <a href="#" className="flex items-center cursor-pointer">
          <img
            src="https://public.readdy.ai/ai/img_res/dfcf48ea-5680-4d6a-82aa-016ba692e447.png"
            alt="GeoEng Systems Logo"
            className={`h-14 w-auto object-contain transition-all duration-300 ${scrolled ? '' : 'brightness-0 invert'}`}
          />
        </a>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className={`text-sm font-medium tracking-wide transition-colors cursor-pointer whitespace-nowrap ${
                scrolled ? 'text-gray-600 hover:text-teal-600' : 'text-white/90 hover:text-white'
              }`}
            >
              {link.label}
            </button>
          ))}
        </nav>

        {/* CTA + Admin */}
        <div className="hidden md:flex items-center gap-2">
          <button
            onClick={() => handleNav('#contact')}
            className="bg-teal-600 hover:bg-teal-700 text-white text-sm font-semibold px-5 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap"
          >
            Request Consultation
          </button>
          <button
            onClick={() => isAdmin ? navigate('/admin') : setShowLoginModal(true)}
            className={`flex items-center gap-1.5 text-sm font-semibold px-4 py-2.5 rounded-full transition-colors cursor-pointer whitespace-nowrap border ${
              scrolled
                ? 'border-teal-200 text-teal-600 hover:bg-teal-50'
                : 'border-white/30 text-white/90 hover:border-white hover:text-white'
            }`}
          >
            <i className={isAdmin ? 'ri-settings-3-line' : 'ri-lock-line'}></i>
            {isAdmin ? 'Admin' : 'Admin Login'}
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          className={`md:hidden text-2xl cursor-pointer ${scrolled ? 'text-gray-800' : 'text-white'}`}
          onClick={() => setMenuOpen(!menuOpen)}
        >
          <i className={menuOpen ? 'ri-close-line' : 'ri-menu-line'}></i>
        </button>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-6 py-4 flex flex-col gap-4">
          {navLinks.map((link) => (
            <button
              key={link.label}
              onClick={() => handleNav(link.href)}
              className="text-gray-700 font-medium text-sm text-left cursor-pointer hover:text-teal-600 transition-colors"
            >
              {link.label}
            </button>
          ))}
          <button
            onClick={() => handleNav('#contact')}
            className="bg-teal-600 text-white text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap w-fit"
          >
            Request Consultation
          </button>
          <button
            onClick={() => { setMenuOpen(false); isAdmin ? navigate('/admin') : setShowLoginModal(true); }}
            className="flex items-center gap-1.5 border border-teal-200 text-teal-600 text-sm font-semibold px-5 py-2.5 rounded-full cursor-pointer whitespace-nowrap w-fit hover:bg-teal-50 transition-colors"
          >
            <i className={isAdmin ? 'ri-settings-3-line' : 'ri-lock-line'}></i>
            {isAdmin ? 'Admin Panel' : 'Admin Login'}
          </button>
        </div>
      )}
    </header>
  );
}
